import React from 'react';
import { Linkedin, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react';
import logo from '../images/trasperentlogo.png';
import { useNavigate } from 'react-router-dom';

const Footer = () => {
    const navigate = useNavigate();

    const handleServiceClick = (path) => {
        navigate(path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="bg-[#0b0f19] text-slate-400 border-t border-slate-800/80 pt-12 pb-6 px-6 md:px-12 mt-auto">
            <div className="container mx-auto max-w-7xl">
                <section data-aos="fade-up" className="flex flex-col lg:flex-row justify-between gap-12">
                    
                    {/* Left Column - Brand & Info */}
                    <div className="w-full lg:w-2/3">
                        
                        {/* Company Logo and Title */}
                        <div className="flex items-center gap-4 mb-8">
                            <img src={logo} alt="Yaniva Ventures Logo" className="w-10 h-10 object-contain hover:scale-105 transition-transform" />
                            <h2 className="text-lg font-bold tracking-wider text-white">
                                YANIVA VENTURES PRIVATE LIMITED
                            </h2>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                            
                            {/* Quick Links Column */}
                            <div className="text-left">
                                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
                                    Our Services
                                </h3>
                                <div className="flex flex-col space-y-2.5 text-sm">
                                    <button
                                        className="hover:text-cyan-400 transition-colors text-left font-medium"
                                        onClick={() => handleServiceClick('/sme')}
                                    >
                                        SME IPO Solutions
                                    </button>
                                    <span className="text-slate-500 cursor-default">IPO Advisory</span>
                                    <span className="text-slate-500 cursor-default">Financial Structuring</span>
                                    <span className="text-slate-500 cursor-default">Mergers & Acquisitions</span>
                                    <span className="text-slate-500 cursor-default">Investor Relations</span>
                                </div>
                            </div>

                            {/* Contact Info Column */}
                            <div className="text-left">
                                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
                                    Contact Details
                                </h3>
                                <div className="space-y-4 text-sm">
                                    <div className="flex items-start gap-3">
                                        <span className="text-cyan-500 shrink-0 mt-0.5"><Mail size={16} /></span>
                                        <a href="mailto:info@yanivaventures.com" className="hover:text-cyan-400 transition-colors">
                                            info@yanivaventures.com
                                        </a>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-cyan-500 shrink-0 mt-0.5"><Phone size={16} /></span>
                                        <a href="tel:+919106930955" className="hover:text-cyan-400 transition-colors">
                                            +91 91069 30955
                                        </a>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-cyan-500 shrink-0 mt-0.5"><MapPin size={16} /></span>
                                        <span className="leading-relaxed">
                                            D-910, Swati Clover, Near Shilaj Circle,<br />
                                            Sardar Patel Ring Road, Ahmedabad - 380 059,<br />
                                            Gujarat, India
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Media & Dividers */}
                        <div className="flex items-center justify-between border-t border-slate-800/60 pt-8 mt-8 flex-wrap gap-4">
                            {/* Social Icons */}
                            <div className="flex gap-4">
                                <a 
                                    href="https://www.linkedin.com/company/yaniva-ventures-pvt-ltd/" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="p-2 bg-slate-800 hover:bg-cyan-500 hover:text-white rounded-xl transition-all duration-300 shadow-sm"
                                    aria-label="LinkedIn"
                                >
                                    <Linkedin size={18} />
                                </a>
                                <a 
                                    href="https://www.instagram.com/yanivaventures/" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="p-2 bg-slate-800 hover:bg-pink-500 hover:text-white rounded-xl transition-all duration-300 shadow-sm"
                                    aria-label="Instagram"
                                >
                                    <Instagram size={18} />
                                </a>
                                <a 
                                    href="https://twitter.com/YanivaVentures" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="p-2 bg-slate-800 hover:bg-sky-400 hover:text-white rounded-xl transition-all duration-300 shadow-sm"
                                    aria-label="Twitter"
                                >
                                    <Twitter size={18} />
                                </a>
                            </div>
                            
                            {/* Legal Links */}
                            <div className="flex items-center gap-6 text-xs text-slate-500">
                                <button onClick={() => handleServiceClick('/privacy-policy')} className="hover:text-slate-300 transition-colors">
                                    Privacy Policy
                                </button>
                                <span>•</span>
                                <button onClick={() => handleServiceClick('/privacy-policy')} className="hover:text-slate-300 transition-colors">
                                    Terms & Conditions
                                </button>
                                <span>•</span>
                                <button onClick={() => handleServiceClick('/privacy-policy')} className="hover:text-slate-300 transition-colors">
                                    Disclaimer
                                </button>
                            </div>
                        </div>
                        
                        <p className="text-[11px] text-slate-600 mt-6 text-left">
                            © {new Date().getFullYear()} Yaniva Ventures Private Limited. All rights reserved.
                        </p>
                    </div>

                    {/* Right Column - Map Embed */}
                    <div className="w-full lg:w-1/3">
                        <div className="h-full rounded-2xl overflow-hidden border border-slate-800/80 shadow-xl bg-slate-900/40 p-2">
                            <iframe
                                src="https://maps.google.com/maps?width=600&amp;height=400&amp;hl=en&amp;q=YANIVA%20VENTURES&amp;t=&amp;z=14&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                                width="100%"
                                height="280"
                                allowFullScreen
                                loading="lazy"
                                className="rounded-xl grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Office Location Map"
                            ></iframe>
                        </div>
                    </div>

                </section>
            </div>
        </div>
    );
};

export default Footer;